import React, { useState, useEffect } from 'react';
import { Key, Copy, Trash2, Plus, Eye, EyeOff } from 'lucide-react';
import * as api from '../services/api';

const TokenManagement = ({ selectedApp, workerApiUrl }) => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [newTokenTtl, setNewTokenTtl] = useState('0'); // Default: never expires
  const [visibleTokens, setVisibleTokens] = useState(new Set());
  const [copiedToken, setCopiedToken] = useState(null);

  useEffect(() => {
    fetchTokens();
  }, [selectedApp.id]);

  const fetchTokens = async () => {
    setLoading(true);
    try {
      const data = await api.getAppTokens(workerApiUrl, selectedApp.id);
      setTokens(data.tokens || []);
    } catch (err) {
      console.error('Failed to fetch tokens:', err);
      // If the endpoint doesn't exist yet, show empty state
      setTokens([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateToken = async (e) => {
    e.preventDefault();
    if (!newTokenName.trim()) return;

    try {
      const newToken = await api.createAppToken(workerApiUrl, selectedApp.id, {
        name: newTokenName.trim(),
        ttl: parseInt(newTokenTtl, 10)
      });
      setTokens([...tokens, newToken]);
      setNewTokenName('');
      setNewTokenTtl('0');
      setShowCreateModal(false);
      
      // Show the new token briefly
      if (newToken.token) {
        alert(`Token created successfully! Make sure to copy it now:\n\n${newToken.token}\n\nYou won't be able to see it again.`);
      }
    } catch (err) {
      alert(`Failed to create token: ${err.message}`);
    }
  };

  const handleDeleteToken = async (tokenId) => {
    if (!confirm('Are you sure you want to delete this token? This action cannot be undone.')) {
      return;
    }

    try {
      await api.deleteAppToken(workerApiUrl, selectedApp.id, tokenId);
      setTokens(tokens.filter(t => t.id !== tokenId));
    } catch (err) {
      alert(`Failed to delete token: ${err.message}`);
    }
  };

  const toggleTokenVisibility = (tokenId) => {
    const newVisible = new Set(visibleTokens);
    if (newVisible.has(tokenId)) {
      newVisible.delete(tokenId);
    } else {
      newVisible.add(tokenId);
    }
    setVisibleTokens(newVisible);
  };

  const copyToClipboard = (token, tokenId) => {
    navigator.clipboard.writeText(token);
    setCopiedToken(tokenId);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const maskToken = (token) => {
    if (!token) return '••••••••••••••••';
    return token.substring(0, 8) + '••••••••' + token.substring(token.length - 4);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="token-management">
      <div className="token-header">
        <div>
          <h2>API Tokens</h2>
          <p className="token-description">
            API tokens allow you to authenticate requests to your application. Keep your tokens secure.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
          <Plus size={18} />
          <span className="btn-text">Create Token</span>
          
        </button>
      </div>

      {loading ? (
        <div className="loading-state">Loading tokens...</div>
      ) : tokens.length > 0 ? (
        <div className="tokens-list">
          {tokens.map(token => (
            <div key={token.id} className="token-card">
              <div className="token-info">
                <div className="token-icon">
                  <Key size={20} />
                </div>
                <div className="token-details">
                  <h4>{token.name}</h4>
                  <div className="token-value">
                    <code>
                      {visibleTokens.has(token.id) 
                        ? token.token 
                        : maskToken(token.token)
                      }
                    </code>
                    <div className="token-value-actions">
                      <button 
                        className="icon-btn"
                        onClick={() => toggleTokenVisibility(token.id)}
                        title={visibleTokens.has(token.id) ? 'Hide' : 'Show'}
                      >
                        {visibleTokens.has(token.id) ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                      <button 
                        className="icon-btn"
                        onClick={() => copyToClipboard(token.token, token.id)}
                        title="Copy to clipboard"
                      >
                        <Copy size={16} />
                        {copiedToken === token.id && <span className="copied-label">Copied!</span>}
                      </button>
                      
                    </div>
                  </div>
                  <div className="token-meta">
                    <span>Created: {formatDate(token.created_at)}</span>
                    {token.expires_at && (
                      <span className={new Date(token.expires_at) < new Date() ? 'text-danger' : ''}>
                        • Expires: {formatDate(token.expires_at)}
                      </span>
                    )}
                    {!token.expires_at && <span> • Never expires</span>}
                    {token.last_used && (
                      <span> • Last used: {formatDate(token.last_used)}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="btn-wrapper">
                <button 
                  className="btn-danger-outline"
                  onClick={() => handleDeleteToken(token.id)}
                >
                  <Trash2 size={16} />
                </button>

              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <Key size={48} />
          <p>No API tokens yet</p>
          <p className="empty-state-hint">
            Create a token to start sending logs to this application
          </p>
          <button className="btn-secondary" onClick={() => setShowCreateModal(true)}>
            Create your first token
          </button>
        </div>
      )}

      {/* Create Token Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create API Token</h2>
              <button 
                className="modal-close" 
                onClick={() => setShowCreateModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateToken}>
              <div className="form-group">
                <label htmlFor="tokenName">Token Name</label>
                <input
                  id="tokenName"
                  type="text"
                  value={newTokenName}
                  onChange={(e) => setNewTokenName(e.target.value)}
                  placeholder="e.g., Production Server"
                  required
                  autoFocus
                />
                <small>Give your token a descriptive name to identify where it's used</small>
              </div>
              <div className="form-group">
                <label htmlFor="tokenTtl">Expiration (TTL)</label>
                <select
                  id="tokenTtl"
                  value={newTokenTtl}
                  onChange={(e) => setNewTokenTtl(e.target.value)}
                >
                  <option value="0">Never expires</option>
                  <option value="86400">24 hours</option>
                  <option value="604800">7 days</option>
                  <option value="2592000">30 days</option>
                  <option value="7776000">90 days</option>
                  <option value="31536000">1 year</option>
                </select>
                <small>The token will automatically expire after this period</small>
              </div>
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={!newTokenName.trim()}
                >
                  Create Token
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenManagement;

